import { inject, Injectable } from '@angular/core';
import {
  IKeyValue,
  IUserContext,
} from '@epicuro-next/models/platform/monitoring';
import { ITelemetryItem } from '@microsoft/applicationinsights-web';

import { AbstractContextService } from '../../abstract/abstract-context.service';

import { AppInsightsService } from './app-insights.service';

/**
 * Context service for App Insights logger
 */
@Injectable()
export class AppInsightsContextService extends AbstractContextService {
  private appInsightsService = inject(AppInsightsService);

  /** Telemetry data to pass to the AppInsights Service */
  private telemetryData: { [key: string]: string | number } = {};

  /**
   * Constructor. Adds telemetry to App Insights service
   * @param appInsightsService
   */
  constructor() {
    super();
    const appInsightsService = this.appInsightsService;

    appInsightsService.instance.addTelemetryInitializer(
      this.telemetryInitializer.bind(this),
    );
  }

  /**
   * Sets the authenticated user information. Pass null to clear it (e.g. on logout).
   *
   * Uses `setAuthenticatedUserContext` so the real user id is stored in the
   * `ai.user.authUserId` field (queryable as `user_AuthenticatedId`) rather than
   * the anonymous, auto-generated `user_Id`. The account id populates the
   * `ai.user.accountId` dimension for B2B correlation, and persisting in a cookie
   * keeps subsequent telemetry (including after reloads) tagged with the user.
   * @param data
   */
  public setUser(data: IUserContext | null) {
    if (data?.id) {
      const sanitize = (value: string): string =>
        value.replace(/[,;=| ]+/g, '_');
      this.appInsightsService.instance.setAuthenticatedUserContext(
        sanitize(data.id),
        data.accountId ? sanitize(data.accountId) : undefined,
        true,
      );
    } else {
      this.appInsightsService.instance.clearAuthenticatedUserContext();
    }
  }

  /**
   * Sets the tags for the app
   * @param tags
   */
  public setTags(data: IKeyValue) {
    Object.keys(data).forEach((key: string) => {
      const value = data[key];
      if (value === null || value === undefined) {
        delete this.telemetryData[key];
      } else {
        this.telemetryData[key] = value;
      }
    });
  }
  /**
   * Sets the release version to log
   * @param data
   */
  public setRelease(data: string) {
    this.appInsightsService.instance.context.application.ver = data;
  }

  /**
   * Initializes the telemetry and assigns item.data
   * @param item
   */
  private telemetryInitializer(item: ITelemetryItem): boolean | void {
    const telemetryData: IKeyValue = {};
    Object.keys(this.telemetryData).forEach((key: string) => {
      telemetryData[key] = this.telemetryData[key];
    });

    if (!item.data) {
      item.data = {};
    }
    item.data = { ...item.data, ...telemetryData };
  }
}
